// @mentions in comment boxes. Typing @ opens a list of program members; picking one writes
// "@Name" in the box, and the comment is sent with each pick encoded as @[Name](userId) so
// the server notifies exactly that person. Posted comments show mentions as bold names.
import { Fragment, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMe } from '@/lib/auth';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Textarea } from './field';

const TOKEN = /@\[([^\]\n]{1,120})\]\(([0-9a-f-]{36})\)/g;

/** Text in the box plus who was picked, encoded on send. */
export function useMentions() {
  const [text, setText] = useState('');
  const picked = useRef(new Map<string, string>());
  /** The comment as sent: each picked "@Name" becomes @[Name](id). Longest names first. */
  const body = () =>
    [...picked.current.entries()]
      .sort(([a], [b]) => b.length - a.length)
      .reduce((t, [name, id]) => t.split(`@${name}`).join(`@[${name}](${id})`), text.trim());
  const reset = () => {
    setText('');
    picked.current.clear();
  };
  return { text, setText, picked, body, reset };
}

type Mentions = ReturnType<typeof useMentions>;

/** A textarea that offers program members after "@". */
export function MentionTextarea({
  program,
  m,
  className,
  ...rest
}: {
  program: Program;
  m: Mentions;
  className?: string;
  rows?: number;
  placeholder?: string;
  'aria-label'?: string;
  id?: string;
}) {
  const me = useMe().data?.user.id;
  const members = useTracker(program.id).data?.members;
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const options = useMemo(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    return (members ?? [])
      .filter((x) => x.role !== 'VENDOR' && x.userId !== me)
      .filter(
        (x) =>
          !q ||
          x.name
            .toLowerCase()
            .split(/\s+/)
            .some((w) => w.startsWith(q)) ||
          x.name.toLowerCase().startsWith(q),
      )
      .slice(0, 6);
  }, [query, members, me]);

  const detect = (value: string, caret: number) => {
    const hit = /(^|\s)@([^@\n]{0,30})$/.exec(value.slice(0, caret));
    setQuery(hit ? hit[2]! : null);
    setActive(0);
  };
  const pick = (x: { userId: string; name: string }) => {
    const el = ref.current!;
    const caret = el.selectionStart;
    const before = m.text.slice(0, caret).replace(/@([^@\n]{0,30})$/, `@${x.name} `);
    const next = before + m.text.slice(caret);
    m.picked.current.set(x.name, x.userId);
    m.setText(next);
    setQuery(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!options.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(options[active]!);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setQuery(null);
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        {...rest}
        className={className}
        value={m.text}
        onChange={(e) => {
          m.setText(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
      />
      {options.length > 0 && (
        <ul
          role="listbox"
          aria-label="Mention someone"
          className="absolute left-2 top-full z-50 mt-1 w-64 overflow-hidden rounded-md border border-border bg-surface py-1 text-[13px] shadow-lg"
        >
          {options.map((x, i) => (
            <li
              key={x.userId}
              role="option"
              aria-selected={i === active}
              className={cn('flex cursor-pointer items-center gap-2 px-3 py-1.5', i === active && 'bg-brand-soft')}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(x);
              }}
            >
              <span className="flex-1 truncate">{x.name}</span>
              <span className="text-xs text-muted">{x.role.toLowerCase()}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-[11px] text-subtle">Type @ to mention someone; they get a link by email.</p>
    </div>
  );
}

/** A posted comment, with mentions shown as bold names. */
export function CommentBody({ body, markdown = true }: { body: string; markdown?: boolean }) {
  if (markdown) {
    const text = body.replace(TOKEN, (_, name: string) => `**@${name}**`);
    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
  }
  const parts = body.split(TOKEN);
  // split with two groups yields [text, name, id, text, name, id, ...]
  return (
    <>
      {parts.map((p, i) =>
        i % 3 === 0 ? (
          <Fragment key={i}>{p}</Fragment>
        ) : i % 3 === 1 ? (
          <strong key={i} className="font-semibold">
            @{p}
          </strong>
        ) : null,
      )}
    </>
  );
}
